import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * IconButton, the glyph-only control in the nav and every toolbar.
 *
 * Source selectors: `system.css` `.dsk-iconbtn`, `.dsk-cart-badge`
 * (alias `.dsk-iconbtn__badge`, byte-identical rules upstream) and
 * `.dsk-dot-badge`.
 *
 * Doctrine enforced:
 * - An icon-only control has no visible name, so `label` is mandatory and lands
 *   on `aria-label`. `aria-label` REPLACES the button's contents for assistive
 *   tech, so when you pass a `badge` put the count in the label too:
 *   `label={`Cart, ${n} items`}`.
 * - The badge is ink filled, not accent: a cart count is a command surface, and
 *   accent is reserved for state. The dot badge is the countless variant, for a
 *   bell that knows there is something new but not how much.
 * - Regular is resting, fill is active. Pass `<Icon as={Heart} active />` when
 *   the thing the button owns is in the active state, never on hover.
 *
 *   <IconButton label="Cart, 3 items" badge={3}><Icon as={ShoppingCart} /></IconButton>
 */
export const IconButton = React.forwardRef(function IconButton(
  { label, badge, dot = false, type, children, className, ...rest },
  ref,
) {
  const showBadge = badge !== null && badge !== undefined && badge !== false && badge !== 0;

  return (
    <button
      ref={ref}
      type={type || 'button'}
      aria-label={label}
      className={cn('dsk-iconbtn', className)}
      {...rest}
    >
      {children}
      {showBadge ? <span className="dsk-cart-badge">{badge}</span> : null}
      {dot && !showBadge ? <span className="dsk-dot-badge" /> : null}
    </button>
  );
});
IconButton.displayName = 'IconButton';

export default IconButton;
